/* Importing the necessary libraries and files */
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

/* Importing the prop-types and moment packages. */
import PropTypes from 'prop-types';
import moment from 'moment';

/* Importing the material-ui components. */
import { Grid, Container } from '@mui/material';
import { Masks as MasksIcon, LocalHospital as LocalHospitalIcon, Policy as PolicyIcon } from '@mui/icons-material/';

/* Importing the functions from the Redux slices. */
import { setTime } from '../features/default/defaultSlice';
import { getPatientAll } from '../features/patient/patientSlice';

/* Importing components. */
import Page from '../components/Page';
import { AppWidget, AppWidgetNoIcon } from '../sections/@dashboard/app';

// ----------------------------------------------------------------------

/* A prop type checker. It is used to check the type of the props passed to the component. */
DashboardApp.propTypes = {
  isCovid19Page: PropTypes.bool,
};

/**
 * Renders the DashboardApp component.
 * @param {Object} props - The component props.
 * @param {boolean} props.isCovid19Page - Indicates whether the page is the COVID-19 dashboard page.
 * @returns {JSX.Element} The rendered DashboardApp component.
 */
export default function DashboardApp(props) {
  const { isCovid19Page } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  /* Getting the states from the Redux store. */
  const currentTime = useSelector((state) => state.default.time);
  const { noOfPatients, noOfCovid19Patients, confirmed, probable, suspected } = useSelector((state) => state.patient);

  const [title, setTitle] = useState('Home');

  const clock = useCallback(() => {
    setInterval(() => {
      dispatch(setTime());
    }, 1000);
  }, [dispatch]);

  const initializeData = useCallback(() => {
    setTitle(isCovid19Page ? 'COVID-19 Dashboard' : 'Home');
  }, [isCovid19Page]);

  useEffect(() => {
    clock();
  }, [clock]);

  useEffect(() => {
    dispatch(getPatientAll());
  }, [dispatch]);

  useEffect(() => {
    initializeData();
  }, [initializeData]);

  return (
    <Page title={title}>
      <Container maxWidth="xl">
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4} lg={3} xl={4}>
            <Grid item xs={12} sx={{ pb: 3 }}>
              <AppWidgetNoIcon title="DATE" total={moment().format('YYYY-MM-DD')} color="secondary" />
            </Grid>

            <Grid item xs={12} sx={{}}>
              <AppWidgetNoIcon title="TIME" total={currentTime} color="secondary" />
            </Grid>
          </Grid>

          {isCovid19Page ? (
            <>
              <Grid item xs={6} sm={6} md={4} lg={3} xl={2} sx={{}}>
                <AppWidget
                  title={noOfCovid19Patients > 1 ? 'COVID-19 PATIENTS' : 'COVID-19 PATIENT'}
                  total={noOfCovid19Patients}
                  icon={<MasksIcon />}
                  onClick={() => navigate(`/covid19/patientlist`, { replace: false })}
                  sx={{ boxShadow: 3 }}
                />
              </Grid>

              <Grid item xs={6} sm={6} md={4} lg={3} xl={2} sx={{}}>
                <AppWidget
                  title={confirmed > 1 ? 'CONFIRMED CASES' : 'CONFIRMED CASE'}
                  total={confirmed}
                  color="error"
                  icon={<MasksIcon />}
                />
              </Grid>

              <Grid item xs={6} sm={6} md={4} lg={3} xl={2} sx={{}}>
                <AppWidget
                  title={probable > 1 ? 'PROBABLE CASES' : 'PROBABLE CASE'}
                  total={probable}
                  color="info"
                  icon={<LocalHospitalIcon />}
                />
              </Grid>

              <Grid item xs={6} sm={6} md={4} lg={3} xl={2} sx={{}}>
                <AppWidget
                  title={suspected > 1 ? 'SUSPECTED  CASES' : 'SUSPECTED  CASE'}
                  total={suspected}
                  color="warning"
                  icon={<PolicyIcon />}
                />
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={6} sm={6} md={4} lg={3} xl={2} sx={{}}>
                <AppWidget
                  title={noOfPatients > 1 ? 'PATIENTS' : 'PATIENT'}
                  total={noOfPatients}
                  icon={<MasksIcon />}
                  onClick={() => navigate(`/patient/list`, { replace: false })}
                  sx={{ boxShadow: 3 }}
                  color="secondary"
                />
              </Grid>
            </>
          )}
        </Grid>
      </Container>
    </Page>
  );
}
